import { useState, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon,
  VpnKey as KeyIcon,
  HourglassEmpty as PendingIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../../app/providers/AuthProvider';
import { useRegistrationSettings } from '../../bot-protection/hooks/useRegistrationSettings';
import {
  CaptchaWidget,
  type CaptchaWidgetRef,
} from '../../bot-protection/components/CaptchaWidget';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../lib/firebase/client';

type SignUpFormProps = {
  readonly onSwitchToSignIn: () => void;
  readonly onClose: () => void;
};

const SignUpForm = ({ onSwitchToSignIn, onClose }: SignUpFormProps) => {
  const { signIn, signUpWithEmail } = useAuth();
  const { settings, loading: settingsLoading } = useRegistrationSettings();
  const captchaRef = useRef<CaptchaWidgetRef | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Bot protection fields
  const [inviteCode, setInviteCode] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [registrationStatus, setRegistrationStatus] = useState<
    'idle' | 'pending-approval' | 'approved' | 'rejected'
  >('idle');

  const validateForm = () => {
    if (!displayName.trim()) {
      setError('Display name is required');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!acceptedTerms) {
      setError('You must accept the Terms of Use and Privacy Policy');
      return false;
    }
    // Validate invite code if in invite-only mode
    if (settings?.mode === 'invite-only' && !inviteCode.trim()) {
      setError('Invite code is required');
      return false;
    }
    // Validate request message if in approval mode
    if (settings?.mode === 'approval' && !requestMessage.trim()) {
      setError('Please tell us why you want to join');
      return false;
    }
    return true;
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Step 1: Get CAPTCHA token if enabled
      let captchaToken: string | null = null;
      let captchaVersion: 'v2' | 'v3' | null = null;

      if (settings?.enableCaptcha && captchaRef.current) {
        const captchaResult = await captchaRef.current.execute();
        if (captchaResult) {
          captchaToken = captchaResult.token;
          captchaVersion = captchaResult.version;
        } else {
          setError('CAPTCHA verification failed. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Step 2: Validate invite code if in invite-only mode
      let validatedCodeId: string | null = null;
      if (settings?.mode === 'invite-only') {
        const validateInviteCodeFn = httpsCallable<
          { code: string },
          { valid: boolean; codeId: string }
        >(functions, 'validateInviteCode');

        try {
          const result = await validateInviteCodeFn({ code: inviteCode.trim() });
          if (!result.data.valid) {
            setError('Invalid or expired invite code');
            setLoading(false);
            return;
          }
          // Store the codeId for later use
          validatedCodeId = result.data.codeId;
        } catch (err) {
          const error = err as { message: string };
          setError(error.message || 'Failed to validate invite code');
          setLoading(false);
          return;
        }
      }

      // Step 3: Create the account
      const userCredential = await signUpWithEmail({ email, password, displayName });

      // Step 4: Handle mode-specific post-registration
      if (settings?.mode === 'invite-only' && validatedCodeId) {
        // Use the invite code (with the codeId from validation)
        const useInviteCodeFn = httpsCallable<{ codeId: string }, { success: boolean }>(
          functions,
          'useInviteCode',
        );

        await useInviteCodeFn({ codeId: validatedCodeId });
        setSuccess(true);
        setTimeout(() => onClose(), 3000);
      } else if (settings?.mode === 'approval') {
        // Submit registration request
        const submitRequestFn = httpsCallable<
          {
            requestMessage: string;
            captchaToken?: string;
            captchaVersion?: 'v2' | 'v3';
          },
          { requestId: string }
        >(functions, 'submitRegistrationRequest');

        await submitRequestFn({
          requestMessage: requestMessage.trim(),
          captchaToken: captchaToken || undefined,
          captchaVersion: captchaVersion || undefined,
        });

        setRegistrationStatus('pending-approval');
      } else {
        // Open registration - just show success
        setSuccess(true);
        setTimeout(() => onClose(), 3000);
      }
    } catch (err) {
      const error = err as { code?: string; message: string };

      // Handle specific error codes
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('Email already in use. Please sign in instead.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Use at least 6 characters.');
          break;
        default:
          setError(error.message || 'Failed to create account');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      await signIn();
      onClose();
    } catch (err) {
      const error = err as { message: string };
      setError(error.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  // Show pending approval message
  if (registrationStatus === 'pending-approval') {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <PendingIcon color="warning" sx={{ fontSize: 40 }} />
          <Typography variant="h5" fontWeight="bold">
            Awaiting Approval
          </Typography>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            Your registration request has been submitted successfully!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            An administrator will review your request within 24-48 hours. You'll receive
            an email at <strong>{email}</strong> once your account is approved.
          </Typography>
        </Alert>
        <Button variant="outlined" fullWidth onClick={onClose} sx={{ mt: 2 }}>
          Close
        </Button>
      </Box>
    );
  }

  // Show success message for approved registrations
  if (success) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Check your email
        </Typography>
        <Alert severity="success" sx={{ mt: 2 }}>
          Account created successfully! We've sent a verification email to{' '}
          <strong>{email}</strong>. Please verify your email to continue.
        </Alert>
      </Box>
    );
  }

  // Show loading state while settings are being fetched
  if (settingsLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleEmailSignUp} noValidate>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Create Account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Join the Penticton developer community
      </Typography>

      {/* Registration Mode Notice */}
      {settings?.mode === 'invite-only' && (
        <Alert severity="info" icon={<KeyIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Invite-only registration:</strong> You need a valid invitation code to
            create an account.
          </Typography>
        </Alert>
      )}

      {settings?.mode === 'approval' && (
        <Alert severity="info" icon={<PendingIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Registration requires approval:</strong> Your account will be reviewed
            by an administrator before activation.
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Display Name"
        value={displayName}
        onChange={e => setDisplayName(e.target.value)}
        margin="normal"
        required
        autoComplete="name"
        autoFocus
      />

      <TextField
        fullWidth
        label="Email"
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        margin="normal"
        required
        autoComplete="email"
      />

      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={e => setPassword(e.target.value)}
        margin="normal"
        required
        autoComplete="new-password"
        helperText="Minimum 6 characters"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        label="Confirm Password"
        type={showPassword ? 'text' : 'password'}
        value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)}
        margin="normal"
        required
        autoComplete="new-password"
      />

      {/* Invite Code Field - only shown in invite-only mode */}
      {settings?.mode === 'invite-only' && (
        <TextField
          fullWidth
          label="Invitation Code"
          value={inviteCode}
          onChange={e => setInviteCode(e.target.value.toUpperCase())}
          margin="normal"
          required
          placeholder="Enter your invitation code"
          helperText="Enter the invitation code you received"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <KeyIcon color="action" />
              </InputAdornment>
            ),
          }}
        />
      )}

      {/* Request Message Field - only shown in approval mode */}
      {settings?.mode === 'approval' && (
        <TextField
          fullWidth
          label="Why do you want to join?"
          value={requestMessage}
          onChange={e => setRequestMessage(e.target.value)}
          margin="normal"
          required
          multiline
          rows={3}
          placeholder="Tell us a bit about yourself and why you'd like to join the community"
          helperText="This helps us understand your interest in joining the developer community"
        />
      )}

      {/* CAPTCHA Widget - shown if CAPTCHA is enabled */}
      {settings?.enableCaptcha && (
        <Box sx={{ my: 2 }}>
          <CaptchaWidget ref={captchaRef} />
        </Box>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={acceptedTerms}
            onChange={e => setAcceptedTerms(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2" color="text.secondary" component="span">
            I have read and accept the{' '}
            <Link
              component={RouterLink}
              to="/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              Terms of Use
            </Link>{' '}
            and{' '}
            <Link
              component={RouterLink}
              to="/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              Privacy Policy
            </Link>{' '}
            *
          </Typography>
        }
        sx={{ mt: 2, mb: 1, alignItems: 'flex-start' }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={
          loading ||
          !displayName ||
          !email ||
          !password ||
          !confirmPassword ||
          !acceptedTerms ||
          (settings?.mode === 'invite-only' && !inviteCode.trim()) ||
          (settings?.mode === 'approval' && !requestMessage.trim())
        }
        sx={{ mt: 2, mb: 2 }}
      >
        {loading
          ? settings?.mode === 'approval'
            ? 'Submitting request...'
            : 'Creating account...'
          : settings?.mode === 'approval'
            ? 'Submit Registration Request'
            : 'Create Account'}
      </Button>

      {/* Only show Google Sign-In for open registration mode */}
      {settings?.mode === 'open' && (
        <>
          <Box sx={{ position: 'relative', my: 2 }}>
            <Box
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                position: 'absolute',
                width: '100%',
                top: '50%',
              }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                position: 'relative',
                display: 'inline-block',
                bgcolor: 'background.paper',
                px: 2,
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              OR
            </Typography>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleSignIn}
            disabled={loading}
            sx={{ mb: 2 }}
          >
            Continue with Google
          </Button>
        </>
      )}

      {/* Show explanation for restricted modes */}
      {settings?.mode !== 'open' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {settings?.mode === 'invite-only'
              ? 'Google Sign-In is disabled during invite-only registration. Please use email/password with your invitation code.'
              : 'Google Sign-In is disabled during approval-required registration. Please use email/password to submit your registration request.'}
          </Typography>
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" align="center">
        Already have an account?{' '}
        <Link
          component="button"
          type="button"
          onClick={onSwitchToSignIn}
          sx={{ cursor: 'pointer' }}
        >
          Sign in
        </Link>
      </Typography>
    </Box>
  );
};

export default SignUpForm;
